import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { REQUIRED_RULE } from 'utils/constants';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { IBank } from 'utils/interfaces';
import { numberOnly } from 'utils/inputValidation';

export default function BankModalContent({
  title,
  editData,
  onClose,
  onSuccess,
}: {
  title: string;
  editData?: IBank;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    register,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      ...editData,
      is_interbank: editData?.is_interbank ? 'Si' : 'No',
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);
  const isInterbank = watch(
    'is_interbank',
    editData ? (editData.is_interbank ? 'Si' : 'No') : 'No'
  );

  const submitAction = async (data: any) => {
    setLoading(true);
    let result: any = await (editData
      ? generalApi.put('/bank', {
          ...data,
          bankId: editData._id,
          is_interbank: data.is_interbank === 'Si',
        })
      : generalApi.post('/bank', {
          ...data,
          is_interbank: data.is_interbank === 'Si',
        }));
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(editData ? 'Cambios guardados' : 'Banco creado');
    onSuccess();
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        control={control}
        watch={watch}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  return (
    <form onSubmit={handleSubmit(submitAction)}>
      <div className="bg-primary-600 rounded-t-lg px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
        <div className="mt-3 text-center sm:mt-0 sm:text-left">
          <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
            {title || 'Confirmar acción'}
          </h3>
        </div>
      </div>
      <div className="space-y-4 px-4 py-5 sm:p-6">
        {buildBaseInput('Código', 'text', 'code', REQUIRED_RULE, {})}
        {buildBaseInput('Nombre', 'text', 'name', REQUIRED_RULE, {})}
        {buildBaseInput('Monto Mínimo', 'text', 'min_amount', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('min_amount', numberOnly(e.target.value)),
        })}
        {buildBaseInput('Monto Máximo', 'text', 'max_amount', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('max_amount', numberOnly(e.target.value)),
        })}
        {buildBaseInput('Límite de dígitos ahorros', 'text', 'A_length', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('A_length', numberOnly(e.target.value)),
        })}
        {buildBaseInput('Límite de dígitos corriente', 'text', 'C_length', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('C_length', numberOnly(e.target.value)),
        })}
        {buildBaseInput('Es interbancario', 'select', 'is_interbank', REQUIRED_RULE, {
          options: [{ name: 'Si' }, { name: 'No' }],
        })}
        {isInterbank === 'Si' &&
          buildBaseInput('Mensaje interbancario', 'text', 'message', REQUIRED_RULE, {})}
      </div>
      <div className="bg-gray-50 rounded-b-lg px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <SolidButton
          className="w-auto sm:ml-3"
          isLoading={loading}
          onClick={() => handleSubmit(submitAction)}
        >
          {editData ? 'Guardar cambios' : 'Crear'}
        </SolidButton>
        <button
          type="button"
          onClick={onClose}
          className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
